import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { showSuccess } from '@spike/notifications-action';
import {
    getEnrollmentStatusThunk,
    getPspProfileThunk,
    PaymentsStatus,
    savePspProfileThunk
} from '@spike/payments-action';
import { EnrollmentStatus, PspProfile, PspProfileProcessingMethod } from '@spike/payments-model';
import { useNonInitialEffect } from '@versiondos/hooks';
import Button from 'components/UI/V3/Button/Button';
import DefaultHeader from 'components/UI/V3/DefaultHeader/DefaultHeader';
import Spinner from 'components/UI/V3/Spinner';
import { useApiClientWrapper } from 'hooks';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { RootState } from 'store';
import { isNewAccount } from 'utils/PaymentsUtils';
import { formatProcessingCharge } from './formatProcessingCharge';
import styles from './PaymentProcessing.module.scss';
import ProcessingMethodCard from './ProcessingMethodCard';
import TaxOnSurcharge from './TaxOnSurcharge';

const FEE_DESCRIPTION = 'Payment processing fee per transaction online or in-store, to be paid by your business.';
const SURCHARGE_DESCRIPTION = "Surcharge added to your client's card payment to cover the payment processing fee.";

const formatSurchargeTaxRate = (rate: number): string => {
    if (!Number.isFinite(rate)) {
        return '';
    }

    return Number(rate.toFixed(2)).toString();
};

const parseSurchargeTaxRate = (value: string): number => {
    if (!value || value === '.') {
        return 0;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : 0;
};

const isCuddlesPayActive = (status: EnrollmentStatus | undefined): boolean => {
    if (!status) {
        return false;
    }

    return isNewAccount(status) ? status.verificationStatus === 'valid' : status.enabled;
};

export const PaymentProcessing: FunctionComponent = () => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const paymentsStatus = useSelector<RootState, PaymentsStatus>(state => state.payments.status);
    const enrollmentStatus = useSelector<RootState, EnrollmentStatus | undefined>(
        state => state.payments.enrollmentStatus
    );
    const pspProfile = useSelector<RootState, PspProfile | undefined>(state => state.payments.pspProfile);

    const enrollmentResolved = useRef(false);
    const [isLoading, setIsLoading] = useState(true);
    const [cuddlesPayActive, setCuddlesPayActive] = useState<boolean | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [processingMethod, setProcessingMethod] = useState<PspProfileProcessingMethod>('fee');
    const [surchargeTaxRate, setSurchargeTaxRate] = useState('');
    const [taxInputKey, setTaxInputKey] = useState(0);

    useEffect(() => {
        setIsLoading(true);
        dispatch(getEnrollmentStatusThunk(apiClientWrapper));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useNonInitialEffect(() => {
        switch (paymentsStatus) {
            case PaymentsStatus.GettingEnrollmentStatus:
                setIsLoading(true);
                break;
            case PaymentsStatus.GetEnrollmentStatusSuccess: {
                if (enrollmentResolved.current) {
                    break;
                }

                enrollmentResolved.current = true;
                const active = isCuddlesPayActive(enrollmentStatus);
                setCuddlesPayActive(active);

                if (active) {
                    dispatch(getPspProfileThunk(apiClientWrapper));
                } else {
                    setIsLoading(false);
                }
                break;
            }
            case PaymentsStatus.GettingPspProfile:
                setIsLoading(true);
                break;
            case PaymentsStatus.GetPspProfileSuccess:
                setIsLoading(false);
                if (pspProfile) {
                    setProcessingMethod(pspProfile.availableSurcharge ? pspProfile.processingMethod : 'fee');
                    setSurchargeTaxRate(formatSurchargeTaxRate(pspProfile.surchargeTaxRate));
                    setTaxInputKey(key => key + 1);
                }
                break;
            case PaymentsStatus.SavingPspProfile:
                setIsSaving(true);
                break;
            case PaymentsStatus.SavePspProfileSuccess:
                setIsSaving(false);
                dispatch(showSuccess('Change has been saved'));
                if (pspProfile) {
                    setProcessingMethod(pspProfile.availableSurcharge ? pspProfile.processingMethod : 'fee');
                    setSurchargeTaxRate(formatSurchargeTaxRate(pspProfile.surchargeTaxRate));
                    setTaxInputKey(key => key + 1);
                }
                break;
            case PaymentsStatus.Error:
                setIsLoading(false);
                setIsSaving(false);
                break;
            default:
                break;
        }
    }, [paymentsStatus, enrollmentStatus, pspProfile]);

    const availableSurcharge = pspProfile?.availableSurcharge === true;
    const feeValue = pspProfile ? formatProcessingCharge(pspProfile.fee.rate, pspProfile.fee.fixed) : '';
    const surchargeValue = pspProfile
        ? formatProcessingCharge(pspProfile.surcharge.rate, pspProfile.surcharge.fixed)
        : '';
    const cardsDisabled = isSaving;

    const handleSave = () => {
        if (isSaving || isLoading) {
            return;
        }

        const method: PspProfileProcessingMethod = availableSurcharge ? processingMethod : 'fee';

        setIsSaving(true);
        dispatch(
            savePspProfileThunk(
                apiClientWrapper,
                method,
                method === 'fee' ? 0 : parseSurchargeTaxRate(surchargeTaxRate)
            )
        );
    };

    return (
        <div className={styles.container}>
            <DefaultHeader
                title="Payment Processing"
                textBelowTitle="Select the best payment processing option for your business."
            />

            {isLoading ? (
                <div className={styles.loading}>
                    <Spinner size={36} />
                </div>
            ) : cuddlesPayActive === false ? (
                <div className={styles.alertContainer}>
                    <span className={styles.icon}>
                        <FontAwesomeIcon icon={faInfoCircle} />
                    </span>
                    <div>
                        <p className={styles.alertText}>
                            <strong>CuddlesPay</strong> is not active on your account. Click below to enroll in
                            CuddlesPay & begin collecting deposits.
                        </p>
                        <Link to="/payments">
                            <Button
                                id="business_settings_payment_processing_button_add_cuddlespay"
                                label="Add CuddlesPay"
                                variant="green"
                            />
                        </Link>
                    </div>
                </div>
            ) : (
                <>
                    <div className={styles.cards}>
                        <ProcessingMethodCard
                            id="business_settings_payment_processing_card_fee"
                            selected={processingMethod === 'fee'}
                            value={feeValue}
                            description={FEE_DESCRIPTION}
                            disabled={cardsDisabled}
                            onSelect={() => setProcessingMethod('fee')}
                        />
                        {availableSurcharge && (
                            <>
                                <span className={styles.or}>or</span>
                                <ProcessingMethodCard
                                    id="business_settings_payment_processing_card_surcharge"
                                    selected={processingMethod === 'surcharge'}
                                    value={surchargeValue}
                                    description={SURCHARGE_DESCRIPTION}
                                    disabled={cardsDisabled}
                                    onSelect={() => setProcessingMethod('surcharge')}
                                />
                            </>
                        )}
                    </div>

                    {availableSurcharge && processingMethod === 'surcharge' && (
                        <TaxOnSurcharge
                            key={taxInputKey}
                            value={surchargeTaxRate}
                            disabled={isSaving}
                            onChange={setSurchargeTaxRate}
                        />
                    )}

                    <Button
                        id="business_settings_payment_processing_button_save"
                        className={styles.saveButton}
                        label="Save"
                        size="large"
                        isLoading={isSaving}
                        disabled={isSaving}
                        onClick={handleSave}
                    />
                </>
            )}
        </div>
    );
};

export default PaymentProcessing;
